import React from "react";
import { useLanguage } from "../contexts/LanguageContext";
import { Link } from "react-router-dom";
import Footer from "../components/Footer";
import LeftSVG from "../assets/page_svg/vector.svg";
import RightSVG from "../assets/page_svg/childrencare-programs.svg";
import WhatsAppButton from "../components/WhatsappButton";

const Services: React.FC = () => {
  const { t } = useLanguage();

  // Helper to get array from translation keys
  const getFeatureList = (key: string) => {
    const value = t(key);
    if (Array.isArray(value)) return value;
    if (typeof value === "string")
      return value
        .split(/,|\n/)
        .map((s) => s.trim())
        .filter(Boolean);
    return [];
  };

  const services = [
    {
      icon: "🏠",
      title: t("services.daycare"),
      description: t("services.daycareDesc"),
      features: getFeatureList("serviceFeaturesList.daycare"),
      price: "€45",
      period: "per day",
      color: "primary",
    },
    {
      icon: "🎒",
      title: t("services.afterSchool"),
      description: t("services.afterSchoolDesc"),
      features: getFeatureList("serviceFeaturesList.afterSchool"),
      price: "€25",
      period: "per day",
      color: "secondary",
    },
    {
      icon: "🏖️",
      title: t("services.holiday"),
      description: t("services.holidayDesc"),
      features: getFeatureList("serviceFeaturesList.holiday"),
      price: "€40",
      period: "per day",
      color: "accent",
    },
    {
      icon: "🎯",
      title: t("services.flexible"),
      description: t("services.flexibleDesc"),
      features: getFeatureList("serviceFeaturesList.flexible"),
      price: "€35",
      period: "per day",
      color: "warm",
    },
  ];

  const additionalServices = [
    {
      icon: "🚌",
      title: t("additionalServices.transportation"),
      description: t("cta.safeTransport"),
      price: "€15/day",
    },
    {
      icon: "🎨",
      title: t("additionalServices.extraActivities"),
      description: t("cta.extraActivities"),
      price: "€20/class",
    },
    {
      icon: "🍎",
      title: t("additionalServices.specialDiet"),
      description: t("cta.specialDiet"),
      price: "€5/day",
    },
    {
      icon: "📚",
      title: t("additionalServices.tutoring"),
      description: t("cta.tutoring"),
      price: "€30/hour",
    },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#F9DCAC" }}>
      <div className="relative z-10">
        {/* Hero Section */}
        <section className="relative py-20 px-4 sm:px-6 lg:px-8 hero-section-h">
          {/* Decorative SVGs (scaled with container) */}
          <div className="absolute inset-0 pointer-events-none z-0">
            {/* Left SVG */}
            <img
              src={LeftSVG}
              alt="Colorful vector decoration for childcare services section"
              className="absolute top-0 left-0 max-w-[150px] sm:max-w-[220px] lg:max-w-[280px] w-full opacity-100"
            />
            {/* Right SVG */}
            <img
              src={RightSVG}
              alt="Childcare programs illustration for services section"
              className="absolute bottom-[-90px] right-0 max-w-[220px] sm:max-w-[220px] md:max-w-[250px] lg:max-w-[400px] w-full opacity-100"
            />
          </div>
          <div className="max-w-6xl mx-auto text-center relative z-10">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-child font-semibold text-black mb-6">
              {t("services.title")}
            </h1>
            <p className="text-xl md:text-2xl text-neutral-800 max-w-3xl mx-auto leading-relaxed">
              {t("services.subtitle")}
            </p>
          </div>
        </section>

        {/* Main Services Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 hidden">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-child font-semibold text-black mb-6">
                {t("cta.ourCoreServices")}
              </h2>
              {/* <p className="text-xl text-neutral-800 max-w-3xl mx-auto leading-relaxed">
                {t("services.mainServicesDesc")}
              </p> */}
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {services.map((service, index) => (
                <div
                  key={index}
                  className="bg-[#FBEACD] rounded-3xl p-8 shadow-soft hover:shadow-warm transition-all duration-300 transform hover:scale-105 "
                >
                  <div className="text-5xl mb-6">{service.icon}</div>
                  <div className="min-h-44">
                    <h3 className="text-2xl font-child font-semibold text-black mb-4">
                      {service.title}
                    </h3>
                    <p className="text-neutral-800 leading-relaxed">
                      {service.description}
                    </p>
                  </div>
                  <div className="mb-6">
                    <span className="text-3xl font-child font-semibold text-[#492A19] block text-center">
                      {service.price}
                    </span>
                    <span className=" ml-1 text-sm font-light text-neutral-500">
                      {service.period}
                    </span>
                  </div>
                  <ul className="space-y-2 ">
                    {service.features.map(
                      (feature: string, featureIndex: number) => (
                        <li key={featureIndex} className="flex items-start">
                          <span className="text-warm-500 mr-2">✓</span>
                          <span className="text-neutral-800 text-sm">
                            {feature}
                          </span>
                        </li>
                      )
                    )}
                  </ul>
                  {/* <button className="w-full bg-[#FBEACD] text-[#492A19] font-child font-semibold py-3 px-6 rounded-2xl border-2 hover:bg-[#492A19] hover:text-[#F9DCAC] transition-all duration-300"
                  style={{ borderColor: '#492A19' }}>
                  {t('services.learnMore')}
                </button> */}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Additional Services Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-child font-semibold text-black mb-6">
                {t("cta.additionalServices")}
              </h2>
              <p className="text-xl text-neutral-800 max-w-3xl mx-auto leading-relaxed">
                {t("cta.additionalServicesSubtitle")}
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {additionalServices.map((service, index) => (
                <div
                  key={index}
                  className="bg-[#FBEACD] rounded-3xl p-8 shadow-soft hover:shadow-warm transition-all duration-300 transform hover:scale-105  text-center"
                >
                  <div className="text-5xl mb-6">{service.icon}</div>
                  <h3 className="text-xl font-child font-semibold text-black mb-4">
                    {service.title}
                  </h3>
                  <p className="text-neutral-800 mb-4 leading-relaxed min-h-20">
                    {service.description}
                  </p>
                  {/* <div className="text-2xl font-child font-semibold text-[#492A19]">
                    {service.price}
                  </div> */}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Service Features Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-child font-semibold text-black mb-6">
                {t("cta.whyChooseOurServices")}
              </h2>
              <p className="text-xl text-neutral-800 max-w-3xl mx-auto leading-relaxed">
                {t("cta.moreThanChildcare")}
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-[#FBEACD] rounded-3xl p-8 shadow-soft hover:shadow-warm transition-all duration-300  text-center">
                <div className="text-5xl mb-6">👩‍🏫</div>
                <h3 className="text-xl font-child font-semibold text-black mb-4">
                  {t("serviceFeatures.qualifiedStaff")}
                </h3>
                <p className="text-neutral-800 leading-relaxed">
                  {t("serviceFeatures.qualifiedStaffDesc")}
                </p>
              </div>

              <div className="bg-[#FBEACD] rounded-3xl p-8 shadow-soft hover:shadow-warm transition-all duration-300  text-center">
                <div className="text-5xl mb-6">🏠</div>
                <h3 className="text-xl font-child font-semibold text-black mb-4">
                  {t("serviceFeatures.safeEnvironment")}
                </h3>
                <p className="text-neutral-800 leading-relaxed">
                  {t("serviceFeatures.safeEnvironmentDesc")}
                </p>
              </div>

              <div className="bg-[#FBEACD] rounded-3xl p-8 shadow-soft hover:shadow-warm transition-all duration-300  text-center">
                <div className="text-5xl mb-6">🍎</div>
                <h3 className="text-xl font-child font-semibold text-black mb-4">
                  {t("serviceFeatures.healthyMeals")}
                </h3>
                <p className="text-neutral-800 leading-relaxed">
                  {t("serviceFeatures.healthyMealsDesc")}
                </p>
              </div>

              <div className="bg-[#FBEACD] rounded-3xl p-8 shadow-soft hover:shadow-warm transition-all duration-300  text-center">
                <div className="text-5xl mb-6">📚</div>
                <h3 className="text-xl font-child font-semibold text-black mb-4">
                  {t("serviceFeatures.learningProgram")}
                </h3>
                <p className="text-neutral-800 leading-relaxed">
                  {t("serviceFeatures.learningProgramDesc")}
                </p>
              </div>

              <div className="bg-[#FBEACD] rounded-3xl p-8 shadow-soft hover:shadow-warm transition-all duration-300  text-center">
                <div className="text-5xl mb-6">⏰</div>
                <h3 className="text-xl font-child font-semibold text-black mb-4">
                  {t("serviceFeatures.flexibleHours")}
                </h3>
                <p className="text-neutral-800 leading-relaxed">
                  {t("serviceFeatures.flexibleHoursDesc")}
                </p>
              </div>

              <div className="bg-[#FBEACD] rounded-3xl p-8 shadow-soft hover:shadow-warm transition-all duration-300  text-center">
                <div className="text-5xl mb-6">👨‍👩‍👧‍👦</div>
                <h3 className="text-xl font-child font-semibold text-black mb-4">
                  {t("serviceFeatures.parentInvolvement")}
                </h3>
                <p className="text-neutral-800 leading-relaxed">
                  {t("serviceFeatures.parentInvolvementDesc")}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-child font-semibold text-black mb-6">
              {t("cta.readyToGetStarted")}
            </h2>
            <p className="text-xl text-neutral-800 mb-8 leading-relaxed">
              {t("cta.contactToLearnMore")}
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-3 bg-[#FBEACD] text-[#492A19] font-child font-semibold text-lg rounded-full shadow-warm hover:bg-[#492A19] hover:text-[#F9DCAC] transition-all duration-300 transform hover:scale-105 border-2"
                style={{ borderColor: "#492A19" }}
              >
                {t("services.contactUs")}
              </Link>
              <Link
                to="/register"
                className="inline-flex items-center justify-center px-8 py-3 bg-[#FBEACD] text-[#492A19] font-child font-semibold text-lg rounded-full border-2 hover:bg-[#492A19] hover:text-[#F9DCAC] transition-all duration-300 transform hover:scale-105"
                style={{ borderColor: "#492A19" }}
              >
                {t("cta.registerNow")}
              </Link>
            </div>
          </div>
        </section>
      </div>

      {/* WhatsApp Floating Contact Button */}
      <WhatsAppButton />
      <Footer />
    </div>
  );
};

export default Services;
